import { defineStore } from 'pinia'
import type { WorkspaceFolderState } from './helper'
import { defaultWorkspaceFolderState, getLocalState, removeLocalState, setLocalState } from './helper'
import { createWorkSpaceFolder, deleteWorkSpaceFolder, getListWorkSpaceFolder, updateWorkSpaceFolder } from '@/api/folder'
import type { WorkspaceFolderDetail } from '@/typings/workspaceFolder'
import { useUserStore } from '@/store/modules/user'

export const useWorkspaceFolderStore = defineStore('workspaceFolder-store', {
  state: (): WorkspaceFolderState => getLocalState(),

  getters: {
    getFolders: state => state.folders,
    getCurrentWorkspaceId: state => state.currentWorkspaceId,
    getUserRole: () => {
      const userStore = useUserStore()
      return userStore.role
    },
  },

  actions: {
    async fetchFolders(workspaceId: number) {
      try {
        const res = await getListWorkSpaceFolder({ workspace_id: workspaceId, page: 1, pageSize: 100 })
        if (res?.data?.items) {
          this.folders = res.data.items
          this.currentWorkspaceId = workspaceId
          this.recordState()
        }
        return this.folders
      }
      catch (error) {
        console.error('Failed to fetch workspace folders:', error)
        return []
      }
    },

    async createFolder(name: string, workspaceId: number, folderDefaultClassroomAgent = false) {
      // Kiểm tra quyền trước khi tạo folder
      // if (!this.checkPermission('FOLDER.CREATE')) {
      //   console.error('Permission denied: Cannot create folder')
      //   return null
      // }

      try {
        const res = await createWorkSpaceFolder({ name, workspace_id: workspaceId, folder_default_classroom_agent: folderDefaultClassroomAgent })
        if (res.data.workspace_folder) {
          const newFolder = res.data.workspace_folder
          this.addFolder(newFolder)
          return newFolder
        }
        return null
      }
      catch (error) {
        console.error('Failed to create workspace folder:', error)
        return null
      }
    },

    async updateFolderName(folderId: number, name: string, workspaceId: number) {
      // Kiểm tra quyền trước khi cập nhật folder
      // if (!this.checkFolderPermission('FOLDER.EDIT', folderId)) {
      //   console.error('Permission denied: Cannot edit folder')
      //   return false
      // }

      try {
        const res = await updateWorkSpaceFolder({
          workspace_folder_id: folderId,
          name,
          workspace_id: workspaceId,
        })

        if (res.data.workspace_folder) {
          // Cập nhật vào state
          this.updateFolder(folderId, res.data.workspace_folder)
          return true
        }
        return false
      }
      catch (error) {
        console.error('Failed to update workspace folder:', error)
        return false
      }
    },

    addFolder(folder: WorkspaceFolderDetail) {
      this.folders.push(folder)
      this.recordState()
    },

    updateFolder(folderId: number, updateData: Partial<WorkspaceFolderDetail>) {
      this.folders = this.folders.map(folder =>
        folder.id === folderId ? { ...folder, ...updateData } : folder,
      )
      this.recordState()
    },

    setFolders(folders: WorkspaceFolderDetail[]) {
      this.folders = folders
      this.recordState()
    },

    async removeFolder(folderId: number) {
      // Kiểm tra quyền trước khi xóa folder
      // if (!this.checkFolderPermission('FOLDER.DELETE', folderId)) {
      //   console.error('Permission denied: Cannot delete folder')
      //   return false
      // }

      try {
        const res = await deleteWorkSpaceFolder({ workspace_folder_id: folderId })

        if (res.data) {
          this.folders = this.folders.filter(f => f.id !== folderId)
          this.recordState()
          return true
        }
        return false
      }
      catch (error) {
        console.error('Failed to delete workspace folder:', error)
        return false
      }
    },

    pinFolder(folderId: number) {
      this.folders = this.folders.map(folder =>
        folder.id === folderId ? { ...folder, isPinned: true } : folder,
      )
      // Reorder folders to put pinned ones at the top
      this.folders.sort((a, b) => {
        if (a.isPinned && !b.isPinned)
          return -1
        if (!a.isPinned && b.isPinned)
          return 1
        return 0
      })
      this.recordState()
    },

    unpinFolder(folderId: number) {
      this.folders = this.folders.map(folder =>
        folder.id === folderId ? { ...folder, isPinned: false } : folder,
      )
      // Reorder folders to put pinned ones at the top
      this.folders.sort((a, b) => {
        if (a.isPinned && !b.isPinned)
          return -1
        if (!a.isPinned && b.isPinned)
          return 1
        return 0
      })
      this.recordState()
    },

    resetFolders() {
      this.$state = defaultWorkspaceFolderState()
      removeLocalState()
    },

    recordState() {
      setLocalState(this.$state)
    },

    // // Kiểm tra quyền dựa trên role
    // checkPermission(permission: string): boolean {
    //   const userRole = this.getUserRole
    //   if (!userRole) return false

    //   // Nếu là admin, cho phép tất cả
    //   if (userRole.role === 'admin') {
    //     return true
    //   }

    //   // Kiểm tra quyền dựa trên function_id array
    //   if (userRole.function_id && Array.isArray(userRole.function_id)) {
    //     return userRole.function_id.some((func: any) => func.code === permission)
    //   }

    //   return false
    // },

    // Kiểm tra quyền cho folder cụ thể
    // checkFolderPermission(permission: string, folderId: number): boolean {
    //   const userRole = this.getUserRole
    //   if (!userRole) return false

    //   // Nếu là admin, cho phép tất cả
    //   if (userRole.role === 'admin') {
    //     return true
    //   }

    //   // Kiểm tra quyền dựa trên function_id array và workspace_folder_id
    //   if (userRole.function_id && Array.isArray(userRole.function_id)) {
    //     const functionItem = userRole.function_id.find((func: any) => func.code === permission)

    //     if (functionItem) {
    //       // Nếu workspace_folder_id rỗng, cho phép tất cả folders
    //       if (!functionItem.workspace_folder_id || functionItem.workspace_folder_id.length === 0) {
    //         return true
    //       }

    //       // Kiểm tra folderId có trong danh sách được phép không
    //       return functionItem.workspace_folder_id.includes(folderId)
    //     }
    //   }

    //   return false
    // },

    // Lọc folders dựa trên quyền
    // getFilteredFolders(): WorkspaceFolderDetail[] {
    // const userRole = this.getUserRole
    // if (!userRole) return this.folders

    // // Nếu là admin, trả về tất cả folders
    // if (userRole.role === 'admin') {
    //   return this.folders
    // }

    // Lọc folders dựa trên quyền FOLDER.VIEW
    // return this.folders.filter(folder => {
    //   return this.checkFolderPermission('FOLDER.VIEW', folder.id)
    // })
    // },

    // Helper methods để kiểm tra các quyền cụ thể
    // canViewFolder(): boolean {
    //   return this.checkPermission('FOLDER.VIEW')
    // },

    // canCreateFolder(): boolean {
    //   return this.checkPermission('FOLDER.CREATE')
    // },

    // canEditFolder(): boolean {
    //   return this.checkPermission('FOLDER.EDIT')
    // },

    // canDeleteFolder(): boolean {
    //   return this.checkPermission('FOLDER.DELETE')
    // },

    // canViewAction(): boolean {
    //   return this.checkPermission('ACTION.VIEW')
    // },

    // canViewKnowledge(): boolean {
    //   return this.checkPermission('KNOWLEAGE.VIEW')
    // },

    // Helper methods để kiểm tra quyền cho folder cụ thể
    // canViewFolderById(folderId: number): boolean {
    //   return this.checkFolderPermission('FOLDER.VIEW', folderId)
    // },

    // canEditFolderById(folderId: number): boolean {
    //   return this.checkFolderPermission('FOLDER.EDIT', folderId)
    // },

    // canDeleteFolderById(folderId: number): boolean {
    //   return this.checkFolderPermission('FOLDER.DELETE', folderId)
    // },
  },
})
